import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ShubhHeader from './ShubhHeader';
import ShubhBottomNav from './ShubhBottomNav';
import { 
  ChevronRight, Star, Heart, Clock, Sparkles, 
  Sun, Moon, TrendingUp, Briefcase, Mic,
  Calendar, Hand, Users, BookOpen
} from 'lucide-react';
import '../../styles/shubh-design-system.css';
import { api } from '../../lib/api';
import { useAstroEvents } from '../../hooks/useAstroEvents';
import { requestReading } from '../../lib/predictionClient';

const ShubhDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [dailyPrediction, setDailyPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictionError, setPredictionError] = useState('');
  const [moonSign, setMoonSign] = useState('');
  const { events } = useAstroEvents();
  const nextEvent = events.find(e => new Date(e.starts_at) > new Date());

  const isHindi = language === 'hi';
  const firstName = user?.name?.split(' ')[0] || 'Seeker';

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return isHindi ? 'सुप्रभात' : 'Good Morning';
    if (hour < 17) return isHindi ? 'नमस्कार' : 'Good Afternoon';
    return isHindi ? 'शुभ संध्या' : 'Good Evening';
  };

  // Fetch profiles and prediction
  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true); setPredictionError(''); setDailyPrediction(null);
      try {
        const profilesData = await api('/profiles', { signal: controller.signal });
        const list = Array.isArray(profilesData) ? profilesData : profilesData.profiles || [];
        setProfiles(list);
        const profile = list.find(p => p.profile_id === localStorage.getItem('active_birth_profile')) || list.find(p => p.relationship === 'self') || list[0];
        setActiveProfile(profile || null);
        if (profile) {
          const { chart } = await api(`/kundli/profiles/${profile.profile_id}/chart`, { signal: controller.signal });
          setMoonSign(chart.moon_sign);
          const reading = requestReading({ rashi: chart.moon_sign.toLowerCase(), profile_id: profile.profile_id, period: 'today', language }, { owner: user?.user_id, profile });
          const prediction = await reading.promise;
          if (!controller.signal.aborted) setDailyPrediction(prediction);
        }
      } catch (error) {
        if (error.name !== 'AbortError') setPredictionError(error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [language, user?.user_id]);

  // Get current date formatted
  const getFormattedDate = () => {
    const options = { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    };
    return new Date().toLocaleDateString(isHindi ? 'hi-IN' : 'en-IN', options);
  };

  // Quick Actions
  const quickActions = [
    { 
      icon: Star, 
      label: isHindi ? 'मेरी कुंडली' : 'My Kundli',
      path: '/janam-kundli',
      color: '#F5B942',
      bg: 'linear-gradient(135deg, #FFF8E1 0%, #FFE082 100%)'
    },
    { 
      icon: Clock, 
      label: isHindi ? 'जीवन समयरेखा' : 'Life Timeline',
      path: '/timeline',
      color: '#C84B32',
      bg: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)'
    },
    { 
      icon: Sun, 
      label: isHindi ? 'आज का राशिफल' : "Today's Rashifal",
      path: '/rashifal',
      color: '#F5B942',
      bg: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)'
    },
    { 
      icon: Mic, 
      label: isHindi ? 'वैदिक गुरु' : 'Ask Nakshatra',
      path: '/vedic-guru',
      color: '#EA580C',
      bg: 'linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)'
    },
  ];

  // Explore More cards
  const exploreCards = [
    { icon: Heart, label: isHindi ? 'कुंडली मिलान' : 'Kundli Milan', path: '/kundli-milan', color: '#F6C7D3' },
    { icon: Hand, label: isHindi ? 'हस्तरेखा' : 'Palm Reading', path: '/palm-reading', color: '#A5D6A7' },
    { icon: Calendar, label: isHindi ? 'पंचांग' : 'Panchang', path: '/hindu-calendar', color: '#90CAF9' },
    { icon: Users, label: isHindi ? 'परिवार मिलान' : 'Family Match', path: '/family-compatibility', color: '#CE93D8' },
    { icon: TrendingUp, label: isHindi ? 'वार्षिक भविष्य' : 'Yearly Forecast', path: '/yearly-forecast', color: '#FFE082' },
  ];

  return (
    <div className="shubh-page" style={{ background: 'var(--shubh-ivory)' }}>
      <ShubhHeader title="Nakshatra" />

      <div className="shubh-page-content" style={{ paddingTop: 'var(--space-4)' }}>
        {/* Greeting Section */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-1">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, var(--saffron) 0%, var(--vermilion) 100%)',
                boxShadow: '0 4px 12px rgba(245, 185, 66, 0.3)'
              }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
                {getGreeting()},
              </p>
              <h2 
                className="text-xl font-semibold"
                style={{ color: 'var(--ink-dark)', fontFamily: 'var(--font-display)' }}
              >
                {firstName}
              </h2>
            </div>
          </div>
          <p className="text-sm mt-2" style={{ color: 'var(--ink-light)' }}>
            {isHindi ? 'एक नया दिन, नई संभावनाएं।' : 'A new day, new possibilities.'}
          </p>
        </motion.div>

        {/* Today's Outlook Card */}
        <motion.div
          className="shubh-featured-card mb-6"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          data-testid="todays-outlook-card"
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sun className="w-4 h-4" style={{ color: 'var(--saffron-dark)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--ink-medium)' }}>
                  {isHindi ? 'आज का दृष्टिकोण' : "Today's Outlook"}
                </span>
              </div>
              <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                {getFormattedDate()}
              </p>
            </div>
            {activeProfile && (
              <div 
                className="px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ 
                  background: 'rgba(245, 185, 66, 0.15)',
                  color: 'var(--saffron-dark)'
                }}
              >
                {moonSign || activeProfile.name}
              </div>
            )}
          </div>

          {loading ? (
            <div className="space-y-2">
              <div className="shubh-skeleton h-5 w-3/4" />
              <div className="shubh-skeleton h-4 w-full" />
              <div className="shubh-skeleton h-4 w-2/3" />
            </div>
          ) : (
            <>
              <h3 
                className="text-lg font-medium mb-2"
                style={{ color: 'var(--ink-dark)', fontFamily: 'var(--font-display)' }}
              >
                {dailyPrediction?.title || (predictionError ? 'Reading unavailable' : 'Add your birth profile')}
              </h3>
              <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--ink-medium)' }}>
                {dailyPrediction?.content || predictionError || 'Your rashi and Kundli reading will use your saved birth details.'}
              </p>
              <button
                data-testid="dashboard-full-guidance-button"
                onClick={() => navigate(moonSign ? `/rashifal/${moonSign.toLowerCase()}` : activeProfile ? '/rashifal' : '/profiles')}
                className="flex items-center gap-1 text-sm font-medium"
                style={{ color: 'var(--vermilion)' }}
              >
                {isHindi ? 'पूरा मार्गदर्शन पढ़ें' : 'Read Full Guidance'}
                <ChevronRight className="w-4 h-4" />
              </button>
              {dailyPrediction && <p data-testid="dashboard-prediction-provider" className="text-xs mt-3" style={{ color: 'var(--ink-muted)' }}>Gemini · Interpretive guidance, not guaranteed outcomes.</p>}
            </>
          )}
        </motion.div>

        {/* Coming Up For You */}
        <motion.section 
          className="shubh-section"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="shubh-section-header">
            <h3 className="shubh-section-title">
              {isHindi ? 'आपके लिए आने वाला' : 'Coming Up for You'}
            </h3>
            <button 
              data-testid="dashboard-view-events-button"
              onClick={() => navigate('/timeline')}
              className="shubh-section-link"
            >
              {isHindi ? 'सभी देखें' : 'View All'}
            </button>
          </div>

          {/* Timeline Event Card */}
          <button
            onClick={() => navigate(nextEvent ? `/timeline/${nextEvent.event_id}` : '/timeline')}
            className="shubh-card shubh-card-interactive w-full text-left p-4"
            style={{ borderLeft: '3px solid var(--saffron)' }}
            data-testid="timeline-event-card"
          >
            <div className="flex items-start gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(245, 185, 66, 0.15)' }}
              >
                <Briefcase className="w-5 h-5" style={{ color: 'var(--saffron-dark)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span 
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ 
                      background: 'rgba(245, 185, 66, 0.15)',
                      color: 'var(--saffron-dark)'
                    }}
                  >
                    {isHindi ? 'चंद्र' : 'Lunar'}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--vermilion)' }}>
                    {nextEvent ? new Date(nextEvent.starts_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <h4 className="font-medium mb-1" style={{ color: 'var(--ink-dark)' }}>
                  {nextEvent ? (isHindi ? nextEvent.title_hi : nextEvent.title) : 'Upcoming lunar events'}
                </h4>
                <p className="text-sm" style={{ color: 'var(--ink-light)' }}>
                  {nextEvent?.description || 'View the event timeline'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--ink-muted)' }} />
            </div>
          </button>
        </motion.section>

        {/* Quick Actions */}
        <motion.section 
          className="shubh-section"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((action, index) => (
              <button
                key={action.path}
                onClick={() => navigate(action.path)}
                className="shubh-card shubh-card-interactive p-4 text-left"
                style={{ background: action.bg }}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ 
                    background: 'rgba(255, 255, 255, 0.8)',
                    boxShadow: 'var(--shadow-sm)'
                  }}
                >
                  <action.icon className="w-5 h-5" style={{ color: action.color }} />
                </div>
                <span 
                  className="text-sm font-medium"
                  style={{ color: 'var(--ink-dark)' }}
                >
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </motion.section>

        {/* Explore More */}
        <motion.section 
          className="shubh-section"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <div className="shubh-section-header">
            <h3 className="shubh-section-title">
              {isHindi ? 'और खोजें' : 'Explore More'}
            </h3>
            <button className="shubh-section-link" onClick={() => navigate('/explore')} data-testid="explore-see-all">
              {isHindi ? 'सभी देखें' : 'See All'}
            </button>
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
            {exploreCards.map((card, index) => (
              <button
                key={card.path}
                onClick={() => navigate(card.path)}
                className="shubh-card flex-shrink-0 p-4 text-center"
                style={{ minWidth: '100px' }}
              >
                <div 
                  className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center"
                  style={{ background: card.color }}
                >
                  <card.icon className="w-6 h-6" style={{ color: 'var(--ink-dark)' }} />
                </div>
                <span className="text-xs font-medium" style={{ color: 'var(--ink-dark)' }}>
                  {card.label}
                </span>
              </button>
            ))}
          </div>
        </motion.section>
      </div>

      <ShubhBottomNav />
    </div>
  );
};

export default ShubhDashboard;
